import { config } from './config';
import type { ArtifactName, Job } from './types';

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${config.apiUrl}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: `Bearer ${token}`,
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  });

  if (!response.ok) {
    const message = await response
      .json()
      .then((body: { message?: string }) => body.message)
      .catch(() => undefined);
    throw new ApiError(response.status, message ?? `Request failed with ${response.status}`);
  }
  return (await response.json()) as T;
}

export function listJobs(token: string): Promise<{ jobs: Job[] }> {
  return request<{ jobs: Job[] }>(token, '/jobs');
}

export function getJob(token: string, jobId: string): Promise<Job> {
  return request<Job>(token, `/jobs/${jobId}`);
}

/** What the uploader knows about the meeting that the audio cannot tell us. */
export interface JobHints {
  participants?: string[];
  terms?: string[];
}

export function createJob(
  token: string,
  file: File,
  hints: JobHints = {},
): Promise<{ jobId: string; uploadUrl: string; contentType: string }> {
  return request(token, '/jobs', {
    method: 'POST',
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || 'audio/mpeg',
      sizeBytes: file.size,
      ...hints,
    }),
  });
}

function artifactUrl(token: string, jobId: string, artifact: ArtifactName) {
  return request<{ url: string; fileName: string }>(token, `/jobs/${jobId}/artifacts/${artifact}`);
}

export async function downloadArtifact(
  token: string,
  jobId: string,
  artifact: ArtifactName,
): Promise<void> {
  const { url } = await artifactUrl(token, jobId, artifact);
  // The presigned URL carries Content-Disposition: attachment, so a plain
  // navigation downloads the file instead of rendering it.
  window.location.assign(url);
}

/**
 * Same presigned URL, fetched instead of navigated to - the attachment
 * disposition only applies to a navigation, so this returns the text for
 * rendering in the app.
 */
export async function fetchArtifactText(
  token: string,
  jobId: string,
  artifact: ArtifactName,
): Promise<string> {
  const { url } = await artifactUrl(token, jobId, artifact);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Datei konnte nicht geladen werden (HTTP ${response.status})`);
  }
  return response.text();
}

/**
 * Uploads straight to S3 with the presigned URL. XHR rather than fetch because
 * we want an upload progress bar, which fetch still cannot give us.
 */
export function uploadToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    // Must match the Content-Type the URL was signed with.
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    xhr.addEventListener('load', () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload fehlgeschlagen (HTTP ${xhr.status})`)),
    );
    xhr.addEventListener('error', () => reject(new Error('Upload fehlgeschlagen (Netzwerkfehler)')));
    xhr.addEventListener('abort', () => reject(new Error('Upload abgebrochen')));
    xhr.send(file);
  });
}
